import React from 'react';
import { useSelector } from 'react-redux';
import { Tooltip } from 'antd';
import { TbAlertTriangle } from 'react-icons/tb';
import { RootState } from '../../../store/store';
import { Product } from '../interfaces';
import useIsraelLawAgreement from '../hooks/useIsraelLawAgreement';
import './supplierCancellationPolicyNotice.less';

/**
 * "Not local policy" badge; the full legal notice opens as a tooltip (hover,
 * or tap on touch screens). Purely presentational — callers decide when a
 * product is an Israeli-law one. Also used by compact flight
 * views (e.g. the dynamic-package "change flight" list).
 */
export const SupplierPolicyBadge = () => {
  const { shoppingCartTranslations: t } = useSelector(
    (data: RootState) => data.config.translations
  );

  return (
    <Tooltip
      title={
        <div className="supplier-policy-tooltip__content">
          <div className="supplier-policy-tooltip__title">
            <TbAlertTriangle size={16} aria-hidden />
            <span>{t.supplierPolicyCartLabel}</span>
          </div>
          <p className="supplier-policy-tooltip__text">
            {t.supplierPolicyLegalNotice}
          </p>
        </div>
      }
      color="#fff"
      overlayClassName="supplier-policy-tooltip"
      placement="top"
      trigger={['hover', 'click']}
      // Above the cart drawer / bot panel (z-index up to 9999)
      zIndex={10000}
    >
      <span
        className="supplier-cancellation-policy-notice"
        role="note"
        tabIndex={0}
        aria-label={`${t.supplierPolicyCartLabel}: ${t.supplierPolicyLegalNotice}`}
      >
        <TbAlertTriangle size={14} aria-hidden />
        <span>{t.supplierPolicyCartLabel}</span>
      </span>
    </Tooltip>
  );
};

/**
 * Marks a cart product that follows the supplier's cancellation conditions
 * rather than the standard Israeli cancellation rules — its product_data (or a
 * sub-product's, e.g. a package's flight) carries `is_israel_law_enabled`.
 * Renders nothing for any other product
 * (or when shareB2C.enable_isreal_law is off), so call sites can mount it
 * unconditionally.
 */
const SupplierCancellationPolicyNotice = ({ product }: { product: Product }) => {
  const { hasIsraelLawAgreement } = useIsraelLawAgreement();

  if (!hasIsraelLawAgreement(product)) {
    return null;
  }

  return (
    <div id="obs-cart-supplier-policy-notice">
      <SupplierPolicyBadge />
    </div>
  );
};

export default SupplierCancellationPolicyNotice;
